import { createSelector } from "@reduxjs/toolkit"
import MessageHelpers from "../lib/messageHelpers"
import { GenericMessage } from "./messagesSlice"
import { RootState } from "./store"
import { jid } from "@xmpp/client"
import { JID } from "@xmpp/jid"
import { userJid } from "../jidUtils"
import { isObuRoomGroup } from "../lib/slickAISilence"
import { getTransicsMessagePresentation } from "../lib/transicsMessagePresentation"

const isSlickAIJid = (value?: string) =>
  !!value && /^(assistant|transchat|slickai)@/i.test(value)

export const driversProfiles = createSelector(
  (state: RootState) => state.profiles,
  (profiles) => profiles.filter((p) => p.role === "driver"),
)

export const officeProfiles = createSelector(
  (state: RootState) => state.profiles,
  (profiles) => profiles.filter((p) => p.role === "office"),
)

// Search hits carry only a display name. A name shared by several profiles
// maps to null, so callers never show another person's avatar or role.
export const profilesByUniqueName = createSelector(
  (state: RootState) => state.profiles,
  (profiles) => {
    const byName = new Map<string, RootState["profiles"][number] | null>()
    for (const profile of profiles) {
      if (!profile.name) continue
      byName.set(profile.name, byName.has(profile.name) ? null : profile)
    }
    return byName
  },
)

const findAuthorHelper = (
  profiles: RootState["profiles"],
  message: GenericMessage,
  domain: string,
) => {
  let candidates = [MessageHelpers.fromBareJid(message, domain)]

  if (message.type === "groupchat") {
    if ("author" in message && message.author) {
      candidates = [message.author]
    } else if (!MessageHelpers.senderJid(message)) {
      candidates.push(userJid(message.from))
    }
  }
  return profiles.find((p) => candidates.includes(p.jid))
}

export const findAuthorProfile = (message: GenericMessage, domain: string) => {
  return createSelector(
    (state: RootState) => state.profiles,
    (profiles) => {
      return findAuthorHelper(profiles, message, domain)
    },
  )
}

export const findProfile = (userJid: string | undefined) => {
  if (!userJid) return () => undefined
  let bareJid: string
  try {
    bareJid = jid.parse(userJid).bare().toString()
  } catch {
    return () => undefined
  }
  return (state: RootState) => {
    return state.profiles.find((p) => p.jid === bareJid)
  }
}

export const findSlickAIProfile = createSelector(
  (state: RootState) => state.profiles,
  (state: RootState) => state.roster,
  (profiles, roster) =>
    profiles.find((p) => isSlickAIJid(p.jid)) ||
    roster.items?.find((r) => isSlickAIJid(r.jid)),
)

const findRosterEntry = (roster: RootState["roster"], userJid: string) => {
  let bareJid: string
  try {
    bareJid = jid.parse(userJid).bare().toString()
  } catch {
    return undefined
  }
  return roster.items.find((item) => item.jid === bareJid)
}

const findCommunicatorContactName = (
  communicatorContacts: RootState["communicatorContacts"],
  userJid: string,
) => {
  let bareJid: string
  try {
    bareJid = jid.parse(userJid).bare().toString()
  } catch {
    return undefined
  }
  return communicatorContacts[bareJid]
}

export const messageAuthorName = (
  message: GenericMessage | undefined,
  myJid: JID,
) => {
  return createSelector(
    (state: RootState) => state.profiles,
    (state: RootState) => state.roster,
    (state: RootState) => state.communicatorContacts,
    (state: RootState) => state.accountChats,
    (profiles, roster, communicatorContacts, accountChats) => {
      if (!message) return ""
      let accountChat
      try {
        accountChat = accountChats[jid(message.from).bare().toString()]
      } catch {
        // from may not be a valid JID
      }

      const transicsPresentation =
        "body" in message
          ? getTransicsMessagePresentation(message, accountChat)
          : undefined

      // A structured XMPP author remains authoritative. When the bridge does
      // not provide one, prefer the sender encoded by Transics over the
      // vehicle profile that the forwarder JID can resolve to.
      const hasStructuredAuthor = "author" in message && Boolean(message.author)
      if (!hasStructuredAuthor && transicsPresentation) {
        return transicsPresentation.authorName
      }

      const authorProfile = findAuthorHelper(profiles, message, myJid.domain)
      if (authorProfile) {
        // Reconstruct from components when both are available — this is more
        // reliable than the cached `name` which may have been persisted before
        // all fields were populated (e.g. only the surname was stored).
        if (authorProfile.firstName && authorProfile.lastName) {
          return `${authorProfile.firstName} ${authorProfile.lastName}`
        }
        if (authorProfile.name.length > 0) {
          return authorProfile.name
        }
      }

      if (transicsPresentation) return transicsPresentation.authorName

      let rosterEntry
      try {
        rosterEntry = findRosterEntry(
          roster,
          MessageHelpers.senderJid(message) ?? userJid(message.from),
        )
      } catch {
        // from may not be a valid JID
      }

      if (rosterEntry && rosterEntry.name) {
        return rosterEntry.name
      }

      if (MessageHelpers.isFromForwarder(message)) {
        // OBU-bridged rooms are per-vehicle — the sender is whoever is
        // behind the on-board unit, so sign messages with the room name
        try {
          const roomJid = jid(message.from).bare().toString()
          const roomAccountChat = accountChats[roomJid]
          if (
            roomAccountChat &&
            isObuRoomGroup(roomAccountChat.group) &&
            roomAccountChat.name
          ) {
            return roomAccountChat.name
          }
        } catch {
          // from may not be a valid JID
        }

        const communicatorContactName = findCommunicatorContactName(
          communicatorContacts,
          message.from,
        )
        if (communicatorContactName) return communicatorContactName
      }

      return MessageHelpers.userName(message)
    },
  )
}
